import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal, Table, InputNumber, Button, Alert, Tag, Input, Typography, Space, message } from '../../ui';
import { request } from '../../api/client';

type Props = { warehouse: { id: string; code: string; name: string } | null; onClose: () => void };

type ProductRow = {
  productId: string; sku: string; name: string; groupCode: string | null; variantLabel: string | null;
  uom: string; packSize: number; productDefault: number; level: number; isOverride: boolean;
};
type Resp = { warehouseId: string; warehouse: { id: string; code: string; name: string } | null; products: ProductRow[] };

const num = (v: any) => Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });

/**
 * Warehouse-first minimum-stock editor: pick a warehouse, then set the minimum quantity to maintain
 * for each product variant in THAT warehouse. Blank = use the product's default. Writes the same
 * per-(product, warehouse) levels the low-stock alerts check against.
 */
export function WarehouseReorderModal({ warehouse, onClose }: Props) {
  const qc = useQueryClient();
  const open = !!warehouse;
  const warehouseId = warehouse?.id ? String(warehouse.id) : '';
  // null = use the product default (no override); a number = override.
  const [edits, setEdits] = useState<Record<string, number | null>>({});
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['wh-reorder', warehouseId],
    queryFn: () => request<Resp>({ url: `/products/reorder-by-warehouse/${warehouseId}` }),
    enabled: open && !!warehouseId,
  });

  useEffect(() => {
    if (data) {
      const init: Record<string, number | null> = {};
      for (const p of data.products) init[p.productId] = p.isOverride ? p.level : null;
      setEdits(init);
      setSearch('');
    }
  }, [data]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.products ?? []).filter((p) => !q || p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q));
  }, [data, search]);

  const save = useMutation({
    mutationFn: () => request({
      url: `/products/reorder-by-warehouse/${warehouseId}`,
      method: 'PUT',
      data: { items: (data?.products ?? []).map((p) => ({ productId: p.productId, level: edits[p.productId] ?? null })) },
    }),
    onSuccess: () => {
      message.success('Minimum stock levels saved');
      qc.invalidateQueries({ queryKey: ['wh-reorder', warehouseId] });
      qc.invalidateQueries({ queryKey: ['stock-reorder'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to save'),
  });

  return (
    <Modal
      open={open} onOpenChange={(o) => { if (!o) onClose(); }}
      title={warehouse ? `Minimum stock levels — ${warehouse.name} (${warehouse.code})` : 'Minimum stock levels'}
      width={760}
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>Save levels</Button></>}
    >
      <Alert
        type="info" className="mb-3"
        title="Set the minimum quantity to maintain for each product variant in this warehouse."
        description="Leave a cell blank to use the product's default. Low-stock alerts compare this warehouse's stock against these levels."
      />
      <Input
        placeholder="Search product / SKU" style={{ width: 280, marginBottom: 12 }}
        value={search} onChange={(e) => setSearch(e.target.value)}
      />
      <Table<ProductRow>
        rowKey="productId" size="small" loading={isLoading}
        dataSource={rows}
        pagination={{ pageSize: 50, showSizeChanger: true }}
        locale={{ emptyText: 'No active products' }}
        columns={[
          {
            title: 'Product', dataIndex: 'name',
            render: (v: string, p: ProductRow) => (
              <span>{v}{p.variantLabel ? <Tag tone="green" className="ml-1.5">{p.variantLabel}</Tag> : null}</span>
            ),
          },
          { title: 'SKU', dataIndex: 'sku', width: 150 },
          { title: 'Default', dataIndex: 'productDefault', align: 'right' as const, width: 90, render: (v: any) => num(v) },
          {
            title: `Minimum @ ${warehouse?.code ?? ''}`, width: 240,
            render: (_: any, p: ProductRow) => {
              const val = edits[p.productId];
              return (
                <Space>
                  <InputNumber
                    min={0} step={1} className="w-[140px]"
                    value={val ?? undefined}
                    placeholder={`Default (${p.productDefault})`}
                    onChange={(x) => setEdits((m) => ({ ...m, [p.productId]: x === null || x === undefined ? null : Number(x) }))}
                  />
                  {(val === null || val === undefined)
                    ? <Tag>Default</Tag>
                    : <Button variant="text" size="sm" style={{ padding: 0 }} onClick={() => setEdits((m) => ({ ...m, [p.productId]: null }))}>Clear</Button>}
                </Space>
              );
            },
          },
        ]}
      />
      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
        Only active products are shown. Changes apply to <b>{warehouse?.code}</b> only.
      </Typography.Paragraph>
    </Modal>
  );
}
